import { useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { Upload, X, Link2, Film, ImageIcon, Loader2, GripVertical } from "lucide-react";
import { uploadMedia, mediaUrl, youtubeEmbed, isVideoFile, uploadErrorMessage } from "@/lib/api";

const IMAGE_ACCEPT = "image/*,.heic,.heif";
const VIDEO_ACCEPT = "video/mp4,video/webm,video/quicktime,video/x-m4v,video/ogg";

function Thumb({ url, kind }) {
  const yt = youtubeEmbed(url);
  if (yt) {
    const id = yt.split("/").pop();
    return <img src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`} alt="" className="w-full h-full object-cover"/>;
  }
  if (kind === "video" || isVideoFile(url)) {
    return <video src={mediaUrl(url)} className="w-full h-full object-cover bg-black" muted preload="metadata"/>;
  }
  return <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover"/>;
}

/**
 * Multi-file media field: upload images/videos to the server, or paste external URLs.
 * value: string[] of URLs; onChange(newArray)
 * kind: "image" | "video" | "any"
 */
export default function MediaUploader({ value = [], onChange, kind = "image", folder = "misc", multiple = true, label, hint, testId = "media-uploader" }) {
  const list = Array.isArray(value) ? value : [];
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [urlText, setUrlText] = useState("");
  const [drag, setDrag] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const accept = kind === "image" ? IMAGE_ACCEPT : kind === "video" ? VIDEO_ACCEPT : `${IMAGE_ACCEPT},${VIDEO_ACCEPT}`;

  const doUpload = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setBusy(true); setPct(0);
    try {
      const res = await uploadMedia(multiple ? arr : arr.slice(0, 1), folder, setPct);
      const urls = res.map((r) => r.url);
      onChange(multiple ? [...list, ...urls] : urls.slice(0, 1));
      toast.success(`${urls.length} file${urls.length > 1 ? "s" : ""} uploaded — remember to save`);
    } catch (e) {
      toast.error(uploadErrorMessage(e));
    } finally {
      setBusy(false); setPct(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const addUrl = () => {
    const urls = urlText.split(",").map((s) => s.trim()).filter(Boolean);
    if (!urls.length) return;
    onChange(multiple ? [...list, ...urls] : urls.slice(0, 1));
    setUrlText("");
  };

  const remove = (i) => onChange(list.filter((_, j) => j !== i));

  const move = (from, to) => {
    if (from === null || from === to) return;
    const n = [...list];
    const [x] = n.splice(from, 1);
    n.splice(to, 0, x);
    onChange(n);
  };

  const Icon = kind === "video" ? Film : ImageIcon;

  return (
    <div data-testid={testId}>
      {label && <span className="label">{label}</span>}

      {list.length > 0 && (
        <div className={`grid gap-2 mb-2 ${multiple ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-1 max-w-[220px]"}`}>
          {list.map((u, i) => (
            <div
              key={`${u}-${i}`}
              draggable={multiple}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); move(dragIdx, i); setDragIdx(null); }}
              className="relative aspect-[4/3] rounded-xl overflow-hidden border border-[color:var(--line)] bg-[color:var(--ivory)] group"
            >
              <Thumb url={u} kind={kind}/>
              {multiple && i === 0 && kind === "image" && (
                <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest bg-[color:var(--maroon)] text-[color:var(--cream-primary)] px-1.5 py-0.5 rounded">Cover</span>
              )}
              {multiple && <GripVertical className="absolute top-1 left-1 w-4 h-4 text-white drop-shadow opacity-0 group-hover:opacity-100 cursor-grab"/>}
              <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-rose-600" aria-label="Remove">
                <X className="w-3.5 h-3.5"/>
              </button>
            </div>
          ))}
        </div>
      )}

      {(multiple || list.length === 0) && (
        <div
          onDragOver={(e) => { e.preventDefault(); if (dragIdx === null) setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); if (dragIdx === null) doUpload(e.dataTransfer.files); }}
          className={`rounded-xl border-2 border-dashed px-4 py-4 text-center transition-colors ${drag ? "border-[color:var(--maroon)] bg-[color:var(--ivory)]" : "border-[color:var(--gold)]/60 bg-white"}`}
        >
          <input ref={inputRef} type="file" accept={accept} multiple={multiple} hidden onChange={(e) => doUpload(e.target.files)} data-testid={`${testId}-input`}/>
          {busy ? (
            <div className="flex flex-col items-center gap-2 py-1 text-sm text-[color:var(--maroon)]">
              <Loader2 className="w-5 h-5 animate-spin"/>
              <span>Uploading… {pct}%</span>
              <div className="w-full max-w-xs h-1.5 rounded-full bg-[color:var(--ivory)] overflow-hidden"><div className="h-full bg-[color:var(--maroon)] transition-all" style={{ width: `${pct}%` }}/></div>
            </div>
          ) : (
            <button type="button" onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--maroon)]">
              <Upload className="w-4 h-4"/> Upload {kind === "video" ? "video" : kind === "any" ? "files" : multiple ? "images" : "image"}
              <span className="font-normal text-neutral-500 hidden sm:inline">or drag & drop</span>
            </button>
          )}
          {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
        </div>
      )}

      {(multiple || list.length === 0) && (
        <div className="flex gap-2 mt-2">
          <div className="relative flex-1">
            <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
            <input
              value={urlText}
              onChange={(e) => setUrlText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
              placeholder={kind === "video" ? "…or paste a YouTube / video URL" : "…or paste an image URL"}
              className="input !py-2 !pl-9 text-sm"
            />
          </div>
          <button type="button" onClick={addUrl} className="btn-ghost !py-2 !px-4 text-sm"><Icon className="w-4 h-4"/> Add</button>
        </div>
      )}
    </div>
  );
}

/** Single image field (string value) built on MediaUploader. */
export function ImageField({ value, onChange, folder = "misc", label, hint, testId }) {
  return (
    <MediaUploader
      value={value ? [value] : []}
      onChange={(arr) => onChange(arr[0] || "")}
      kind="image"
      folder={folder}
      multiple={false}
      label={label}
      hint={hint}
      testId={testId}
    />
  );
}

/** Renders a stored video URL: YouTube -> iframe, uploaded/direct file -> <video>. */
export function VideoPlayer({ url, title = "video", className = "w-full h-full" }) {
  const yt = youtubeEmbed(url);
  if (yt) return <iframe src={yt} className={className} allowFullScreen title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"/>;
  if (isVideoFile(url) || (url || "").includes("/api/uploads/")) {
    return <video src={mediaUrl(url)} className={`${className} bg-black object-contain`} controls preload="metadata" playsInline/>;
  }
  return <iframe src={url} className={className} allowFullScreen title={title}/>;
}
